import { z } from 'zod';

/**
 * What the web signup's last-week baseline steps need to know about the
 * member's substance: which programs have a wording of their own, and the
 * shape of each wording's "what counts as one" guide. Structure only: the
 * words (what to count, the guide's title and items, the summary labels and
 * the unit) live in the signup catalog, keyed by program key and item icon, so
 * one structure serves every language. The client picks a wording by the
 * member's primary program, found with the carried priority, and never names a
 * program (ADR-015); a program with no wording of its own reads the default.
 *
 * Client-parsed, so the schemas stay open: an unknown field from a newer
 * server is ignored rather than rejected (ADR-135).
 */
export const signupBaselineEquivalentSchema = z.object({
  /** A design-system icon name; it also keys the item's words. */
  icon: z.string().min(1),
});

export const signupBaselineUnitGuideSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('equivalents'),
    items: z.array(signupBaselineEquivalentSchema).min(1),
  }),
  z.object({
    kind: z.literal('text'),
  }),
]);

export const signupBaselineWordingSchema = z.object({
  unitGuide: signupBaselineUnitGuideSchema.optional(),
});

export const signupBaselineContentSchema = z.object({
  /** Program keys, highest first: the first one the member chose decides the wording. */
  priority: z.array(z.string().min(1)),
  default: signupBaselineWordingSchema,
  wordings: z.record(z.string(), signupBaselineWordingSchema),
});

export type SignupBaselineEquivalent = z.infer<typeof signupBaselineEquivalentSchema>;
export type SignupBaselineUnitGuide = z.infer<typeof signupBaselineUnitGuideSchema>;
export type SignupBaselineWording = z.infer<typeof signupBaselineWordingSchema>;
export type SignupBaselineContent = z.infer<typeof signupBaselineContentSchema>;

/**
 * The alcohol wording is v9's (frames 1.11, 1.11a and 1.12), and so are the
 * two yes/no wordings, which carry their own last-week title. The cannabis
 * wording counts 1-hour blocks, the unit its baseline is stored in. The default is
 * Galapagos onboarding's neutral usage wording. The web signup seam serves it
 * as is, real and local fake alike; an endpoint that later takes over serves
 * the same shape.
 */
export const SIGNUP_BASELINE_CONTENT: SignupBaselineContent = {
  priority: ['opioid', 'stimulants', 'alcohol', 'cannabis', 'tobacco'],
  default: {},
  wordings: {
    alcohol: {
      unitGuide: {
        kind: 'equivalents',
        items: [{ icon: 'alcohol' }, { icon: 'liquor' }, { icon: 'wine' }, { icon: 'beer' }],
      },
    },
    cannabis: { unitGuide: { kind: 'text' } },
    opioid: {},
    stimulants: {},
  },
};
